// The review state under the dialog's title: where the product stands, read
// from the catalog editor, so it follows a decision made in the Product Review
// tab before the dialog is saved. One state at a time, and it opens where it is dealt with: the product's
// own (Draft, In Review, Approved, Rejected, Review Required) opens the Product Review tab, with the count of
// what a reviewer marked; a product on sale with changes waiting for review shows those instead, and opens
// them (ChangeSetDialog: what needs attention, send again, discard).
import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import { ClockIcon, ExclamationTriangleIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline"
import ChangeSetDialog from "./ChangeSetDialog"
import { marksOf, ReviewCountChip, changeLabel } from "../shared/review-marks"
import { ATTENTION_PILL } from "../../../AdminShared/ui"
import { useUrlParam } from "aldoo-ra/URLState"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { useCatalogEditor } from "../shared/catalog-editor"
import PublishStatePill from "../shared/PublishStatePill"
import useReviewSync from "../shared/useReviewSync"
import { EPublishStatus } from "../shared/editor-config"

// the extension's id is its tab's (the dialog keeps the open tab in ?ptab=); a merchant's draft has a
// "Send for Review" beside its state, which opens that tab too
const REVIEW_TAB = "admin-review"

export default function PublishStateSubtitle() {
  const catalog = useCatalogEditor()
  const [, setTab] = useUrlParam("ptab", { default: "" })
  const [, setSubmit] = useUrlParam("psubmit", { default: "" })
  const [open, setOpen] = useState(false)
  const t = useProductEditorTexts()
  // a reviewer's answer arrives while the dialog is open (realtime): the state shown here, and the tabs, follow
  useReviewSync(catalog?.editor)
  if (!catalog?.ready) return null
  const status = catalog.editor.read("publishStatus") ?? EPublishStatus.Draft
  // approved: a merchant has no review left to open (the tab is not theirs then)
  const reviewable = catalog.isAdmin || Number(status) !== EPublishStatus.Published
  const changes = (catalog.editor.read("reviewChanges") || []).map((change) => changeLabel(change, t))
  // the set's state: waiting for a reviewer, or back with the vendor
  const change = catalog.editor.read("reviewChange")
  const marks = marksOf(catalog.editor)
  const back = change?.status === "changes_requested"
  // a merchant's product that can go to a reviewer now: waiting on them, nothing marked left unchanged
  const unchanged = (catalog.editor.product?.lastReview?.attention || []).filter((item) => !item.resolved)
  // (an Aldoo admin too: they may send on the company's behalf, whatever its set-up)
  const sendable =
    [EPublishStatus.Draft, EPublishStatus.Rejected, EPublishStatus.ReviewRequired].includes(Number(status)) &&
    !(Number(status) === EPublishStatus.ReviewRequired && unchanged.length)
  // sent back, and every marked item changed: "Review Required" has nothing left to say — the state itself
  // becomes the way to send it again
  const changesMade = catalog.isProductEditor && Number(status) === EPublishStatus.ReviewRequired && !unchanged.length
  const send = () => {
    setTab(REVIEW_TAB)
    setSubmit("1")
  }
  const sendButton = (
    <Button type="button" variant="admin_primary" onClick={send} className="h-[34px] px-3 text-xs" data-testid="header-send-for-review">
      <PaperAirplaneIcon className="w-4 h-4" />
      {t`Send for Review`}
    </Button>
  )
  // one state at a time: the product's own, or — on sale with changes waiting — its changes. The state opens
  // where it is dealt with: the Product Review tab (with the count of what a reviewer marked), or the changes
  const changesChip = changes.length ? (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={`inline-flex items-center gap-2 h-[34px] px-3 rounded-md border text-xs font-medium whitespace-nowrap hover:opacity-80 ${back ? ATTENTION_PILL : "border-admin_warning text-admin_warning bg-admin_warning/10"}`}
      title={t`Waiting for a reviewer: the product stays on sale as it is until they are approved`}
      data-testid="review-changes-chip"
    >
      {back ? <ExclamationTriangleIcon className="w-4 h-4" /> : <ClockIcon className="w-4 h-4" />}
      {back ? t`Your changes need attention` : t("Changes in review: %s", changes.join(", "))}
      <ReviewCountChip count={marks.length} title={marks.map((mark) => mark.label).join(", ")} />
    </button>
  ) : null
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {changesMade ? (
        sendButton
      ) : (
      <PublishStatePill
        status={status}
        product={catalog.editor.product}
        size="sm"
        onClick={reviewable ? () => setTab(REVIEW_TAB) : undefined}
        badge={<ReviewCountChip count={marks.length} title={marks.map((mark) => mark.label).join(", ")} />}
      >
        {changesChip}
      </PublishStatePill>
      )}
      {/* a merchant's product ready to send — a draft, a rejected one, one sent back with every marked item
          changed: sent from here, whatever tab is open (the review tab sends it, and says what holds it back) */}
      {sendable && !changesMade && sendButton}
      {open && <ChangeSetDialog editor={catalog.editor} isAdmin={catalog.isAdmin} onClose={() => setOpen(false)} />}
    </span>
  )
}
